import { computed, ComputedRef, readonly, ref, unref, watch } from 'vue';
import isEqual from 'lodash.isequal';
import { CODES, useLiveInfiniteQueryV2 } from '@libs/use';
import { NewsItem, NewsQuery } from '@libs/api-reference';
import { MaybeRef } from '@vueuse/core';

import * as API from '../requests';
import { transformSingleNews } from '../utils/transforms';
import { isFakeNews } from '../utils/isFakeNews';
import { NEWS_QUERY_KEYS } from '../constants';

// Bypassing the lack of an appropriate way to determine whether a new item fetched or not.
// The solution take the fact into account that each news has a unique ID.
// So, every time the first fetched items has a different ID, we could presume that a new item fetched.

const isUpdated = ref(false);
const firstItemId = ref<string | null>(null);

watch(firstItemId, (newValue, oldValue) => {
  if (oldValue && newValue !== oldValue) {
    isUpdated.value = true;
  }
});

const resetUpdatedState = () => {
  isUpdated.value = false;
};

export default function useLiveNewsQuery({
  immediate,
  params,
  listen = true,
}: {
  immediate: MaybeRef<boolean>;
  params: ComputedRef<string | any>;
  listen?: boolean;
}) {
  const {
    execute: getNews,
    result,
    isPending: isLoadingNews,
    isSuccess: isSuccessLoadingNews,
    reset,
    isError: isErrorLoadingNews,
    status: newsStatus,
    isFetchingNextPage,
    hasNextPage,
    ...query
  } = useLiveInfiniteQueryV2<NewsQuery, NewsItem>({
    key: NEWS_QUERY_KEYS.news(params),
    immediate,
    channel: listen ? 'mdp-text-message' : undefined,
    fetcher: ({ pageNumber, pageSize }) =>
      API.getNewsList({
        pageNumber,
        pageSize,
        ...unref(params),
      }),
    onSuccess() {
      firstItemId.value = result.value?.[0]?.id;
    },
    onUpdate(signal) {
      const actions = {
        [CODES.NewsContentSignal]: transformSingleNews,
      };

      if (actions[signal.code]) {
        const data = actions[signal.code](signal.data);

        isUpdated.value = true;
        firstItemId.value = data.id;

        if (!isFakeNews(data.messageTitle)) {
          result.value.unshift(data);

          // The problem has been raised after fetch a new list by scrolling, last item of the first list is the same as first item of the second list.
          // A temporary solution is removing the last item of the current list when added a new item by the socket.
          result.value.pop();
        }
      }
    },
  });

  watch(params, (current, previous) => {
    if (!isEqual(current, previous)) {
      reset();
    }
  });

  const news = computed(() => result.value || []);

  const newsWithoutFakeData = computed<NewsItem[]>(() => {
    return news.value.filter((news) =>
      news.messageTitle ? !isFakeNews(news?.messageTitle) : null
    );
  });

  return {
    news,
    newsWithoutFakeData,
    getNews,
    resetUpdatedState,
    hasReceivedNews: readonly(isUpdated),
    isLoadingNews,
    isErrorLoadingNews,
    isSuccessLoadingNews,
    isFetchingNextPage,
    newsStatus,
    hasNextPage,
    reset,
    ...query,
  };
}
